import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate, useForm } from '@ur-solutions/react-hooks'
import {
  Checkbox,
  DatePicker,
  NumberInput,
  RegularModal as BaseRegularModal,
  UserSelect,
  TimePicker,
} from '@/components_deprecated'
import { set, differenceInWeeks, startOfDay } from 'date-fns'
import type {
  VehicleExpirationNode,
  VehicleExpirationTypeShallow,
} from '@/modules/vehicles'
import { Input, Field, Form } from '@/modules/users/user-settings/components'
import type {
  EditVehicleExpirationForm,
  EditVehicleExpirationPromptResolve,
} from '@/modules/vehicles/types'
import styled from 'styled-components'
import { useState } from 'react'
import { VehicleExpirationTypeSelect } from './VehicleExpirationTypeSelect'

const RegularModal = styled(BaseRegularModal)`
  box-shadow: ${props => props.theme.shadow.default};
`

const Wrapper = styled.div`
  padding: 1rem 1rem;
  display: flex;
  justify-content: space-between;
`

const DateEditor = styled.div`
  margin-top: 0.5rem;
  display: flex;

  & > * + * {
    margin-left: 0.5rem;
  }
`

interface EditVehicleExpirationModalProps {
  vehicleExpiration?: VehicleExpirationNode
  onSubmit: PromptResolve<EditVehicleExpirationPromptResolve | null>
}

export const EditVehicleExpirationModal: React.FC<
  EditVehicleExpirationModalProps
> = ({ vehicleExpiration, onSubmit }) => {
  const translations = useTranslate({
    save: 'common.save',
    type: 'common.type',
    startDate: 'common.start-date',
    newVehicleService: 'vehicles.new-vehicle-service',
    vehicleServices: 'vehicles.vehicle-services',
    editVehicleService: 'vehicles.edit-vehicle-service',
    description: 'common.description',
    hasNotification: 'settings.has-notification',
    endDate: 'common.end-date',
    notificationTime: 'vehicles.service.notification-time',
    responsibleUser: 'common.responsible-user',
    validation: {
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
    },
    duration: 'common.duration',
    durationInMinutes: 'common.duration-in-minutes',
    dateRange: 'common.use-date-range',
  })
  const editing = typeof vehicleExpiration !== 'undefined'
  const [hasNotification, setHasNotification] = useState(
    !!vehicleExpiration?.notificationTime || false
  )

  const normalizeExpirationDates = (
    from?: string | null,
    to?: string | null
  ) => {
    const expirationFrom = from ? new Date(from) : new Date()
    const expirationDate = to ? new Date(to) : new Date()

    if (expirationFrom > expirationDate) {
      return { expirationFrom: new Date(expirationDate), expirationDate }
    }

    return { expirationFrom, expirationDate }
  }

  const { expirationFrom, expirationDate } = normalizeExpirationDates(
    vehicleExpiration?.expirationFrom,
    vehicleExpiration?.expirationDate
  )

  const notificationTime =
    !vehicleExpiration?.expirationDate || !vehicleExpiration?.notificationTime
      ? 1
      : differenceInWeeks(
          new Date(vehicleExpiration.expirationDate),
          startOfDay(new Date(vehicleExpiration.notificationTime))
        )

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    submitHandler: submit,
    formChangeHandler: handler,
  } = useForm<EditVehicleExpirationForm>({
    values: {
      description: vehicleExpiration?.description ?? '',
      expirationDate: expirationDate,
      notificationTime: notificationTime,
      expirationType: vehicleExpiration?.expirationType?.id || null,
      expirationFrom: expirationFrom,
      expirationTo: expirationDate,
      responsibleUser: vehicleExpiration?.responsibleUser?.id || null,
      duration: vehicleExpiration?.expirationDuration ?? 0,
    },
    validators: {
      expirationFrom: (value, form) => {
        if (!value || !form.expirationTo) {
          return null
        }
        return value < form.expirationTo
          ? null
          : translations.validation.startBeforeEnd
      },
      expirationTo: (value, form) => {
        if (!value || !form.expirationFrom) {
          return null
        }
        return value > form.expirationFrom
          ? null
          : translations.validation.startBeforeEnd
      },
      expirationType: val => (val ? null : translations.validation.required),
    },
    config: {
      initAsInvalid: true,
    },
  })

  const handleChangeDate = (
    field: 'expirationFrom' | 'expirationTo',
    date: Date
  ) => {
    const current = form[field]
    const newDate = set(date, {
      hours: current.getHours(),
      minutes: current.getMinutes(),
    })
    handler(field)(newDate)
  }

  function handleChangeExpirationType(
    id: string,
    _category: VehicleExpirationTypeShallow | null
  ) {
    handler('expirationType')(id)
  }

  return (
    <RegularModal
      title={
        editing
          ? translations.editVehicleService
          : translations.newVehicleService
      }
      width="480px"
      submitText={translations.save}
      submitDisabled={!formValid}
      onCancel={() => onSubmit(null)}
      onSubmit={submit(form => onSubmit({ form, hasNotification }))}
    >
      <Form>
        <>
          <Field>
            <label>{translations.startDate}</label>
            <DateEditor>
              <TimePicker
                value={form.expirationFrom}
                disabled={form.expirationType === 'nextEuControl'}
                onChange={handler('expirationFrom')}
              />

              <DatePicker
                value={form.expirationFrom}
                disabled={form.expirationType === 'nextEuControl'}
                fullWidth
                onChange={value => handleChangeDate('expirationFrom', value)}
              />
            </DateEditor>
          </Field>

          <Field>
            <label>{translations.endDate}</label>
            <DateEditor>
              <TimePicker
                value={form.expirationTo}
                disabled={form.expirationType === 'nextEuControl'}
                onChange={handler('expirationTo')}
              />
              <DatePicker
                value={form.expirationTo}
                disabled={form.expirationType === 'nextEuControl'}
                fullWidth
                onChange={value => handleChangeDate('expirationTo', value)}
              />
            </DateEditor>
          </Field>
        </>

        <Field>
          <label>{translations.type}</label>
          <VehicleExpirationTypeSelect
            fullWidth
            value={form.expirationType}
            onChange={handleChangeExpirationType}
          />
        </Field>

        <Field>
          <label>{translations.description}</label>
          <Input
            value={form.description}
            error={errors.description}
            disabled={form.expirationType === 'nextEuControl'}
            fullWidth
            autoFocus
            onChange={handler('description')}
          />
        </Field>

        <Wrapper>
          <Field>
            <Checkbox
              checked={hasNotification}
              label={translations.hasNotification}
              boxSize="32px"
              onChange={setHasNotification}
            />
          </Field>
        </Wrapper>

        {hasNotification && (
          <>
            <Field>
              <label>{translations.responsibleUser}</label>
              <UserSelect
                value={form.responsibleUser}
                fullWidth
                onChange={handler('responsibleUser')}
                nullable
              />
            </Field>

            <Field>
              <label>{translations.notificationTime}</label>
              <NumberInput
                value={form.notificationTime ?? 1}
                min={0}
                fullWidth
                onChange={handler('notificationTime')}
              />
            </Field>
          </>
        )}
      </Form>
    </RegularModal>
  )
}
